import React, { useState, useEffect } from 'react';
import apiUsageManager from './ApiUsageManager';

/**
 * Composant affichant le statut d'utilisation de l'API et permettant d'acheter un abonnement premium
 */
const ApiUsageStatus = () => {
  const [usageStats, setUsageStats] = useState(apiUsageManager.getUsageStats());
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  
  // Rafraîchir les statistiques toutes les 30 secondes
  useEffect(() => {
    const updateStats = () => {
      setUsageStats(apiUsageManager.getUsageStats());
    };
    
    const interval = setInterval(updateStats, 30000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Formatage de la date
  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return new Intl.DateTimeFormat('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    }).format(date);
  };
  
  // Gestion de l'activation du compte premium
  const handleActivatePremium = () => {
    // Dans une application réelle, ceci serait connecté à un système de paiement
    apiUsageManager.activatePremium();
    setUsageStats(apiUsageManager.getUsageStats());
    setShowPremiumModal(false);
  };
  
  // Gestion de la désactivation du compte premium (pour la démo)
  const handleDeactivatePremium = () => {
    apiUsageManager.deactivatePremium();
    setUsageStats(apiUsageManager.getUsageStats());
  };
  
  return (
    <div className="mt-4">
      <div className={`p-4 rounded-md ${
        usageStats.remainingCalls === 0 
          ? "bg-red-50 border border-red-200" 
          : usageStats.remainingCalls < 2 
            ? "bg-yellow-50 border border-yellow-200"
            : "bg-blue-50 border border-blue-200"
      }`}>
        <div className="flex justify-between items-start">
          <h3 className={`font-medium ${
            usageStats.remainingCalls === 0 
              ? "text-red-800" 
              : usageStats.remainingCalls < 2 
                ? "text-yellow-800"
                : "text-blue-800"
          }`}>
            Utilisation de l'API d'analyse
          </h3>
          
          {usageStats.isPremium ? (
            <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full font-medium">
              Premium
            </span>
          ) : (
            <button
              onClick={() => setShowPremiumModal(true)}
              className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md"
            >
              Passer au Premium
            </button>
          )}
        </div>
        
        <div className="mt-3 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Analyses ce mois-ci:</span>
            <span className="font-medium">{usageStats.callCount} / {usageStats.isPremium ? '10' : '5'}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Analyses restantes:</span>
            <span className="font-medium">{usageStats.remainingCalls}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Résultats mis en cache:</span>
            <span className="font-medium">{usageStats.cacheHits}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Prochaine réinitialisation:</span>
            <span className="font-medium">{formatDate(usageStats.nextReset)}</span>
          </div>
        </div>
        
        {usageStats.remainingCalls === 0 && !usageStats.isPremium && (
          <div className="mt-3 text-sm text-red-700">
            <p>Vous avez atteint votre limite d'analyses pour ce mois-ci.</p>
            <button
              onClick={() => setShowPremiumModal(true)}
              className="mt-2 text-sm bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md"
            >
              Débloquer plus d'analyses
            </button>
          </div>
        )}
        
        {usageStats.isPremium && (
          <div className="mt-3 text-xs text-gray-500 flex justify-between items-center">
            <span>Abonnement Premium actif</span>
            
            {/* Bouton de désactivation (uniquement pour la démo) */}
            <button
              onClick={handleDeactivatePremium}
              className="text-xs text-gray-500 underline hover:text-gray-700"
            >
              Désactiver (démo)
            </button>
          </div>
        )}
      </div>
      
      {/* Modal d'abonnement Premium */}
      {showPremiumModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-medium">Passer au Premium</h2>
              <button 
                onClick={() => setShowPremiumModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="p-4 bg-purple-50 rounded-md">
                <h3 className="font-medium text-purple-800 mb-2">Abonnement Premium</h3>
                <p className="text-sm text-gray-700 mb-4">
                  Bénéficiez de plus d'analyses d'œuvres d'art par IA et de fonctionnalités exclusives.
                </p>
                
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center">
                    <svg className="h-4 w-4 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>10 analyses par mois (au lieu de 5)</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="h-4 w-4 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Accès à des catégories de tags exclusives</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="h-4 w-4 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Analyse contextuelle avancée</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="h-4 w-4 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Support prioritaire</span>
                  </li>
                </ul>
                
                <div className="mt-4 text-center">
                  <div className="text-lg font-bold text-purple-900">9,99 € / mois</div>
                  <div className="text-xs text-gray-500">Sans engagement</div>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowPremiumModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleActivatePremium}
                  className="px-4 py-2 bg-purple-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-purple-700"
                >
                  Activer Premium
                </button>
              </div>
              
              <div className="text-xs text-gray-500 text-center mt-4">
                * Dans cette démo, l'abonnement est activé sans paiement réel.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiUsageStatus;